'use client'
import { useState } from 'react';
import { motion } from 'framer-motion';
import s from './Grid.module.scss';
import ProjectFeaturedComponent from '@/components/singles/projectFeatured';
import { Status, Region, Typology } from '@/types/singles/category';
import { Image } from '@/types/common/ImageData';

interface ProjectsListProps {
    projects: Array<DataProps>;
    layout: Array<string> | null;
    defaultGrid: boolean | null;
}

interface ImageProp {
    image: Image;
}

interface DataProps {
    title: string;
    slug: string;
    featuredImage: ImageProp | null;
    featuredImageHover: ImageProp | null;
    location: string | null;
    typologies: Array<Typology>;
    status: string | null;
    region: string | null;
}

export default function ProjectsGridComponent({ projects, layout, defaultGrid = true }: ProjectsListProps) {
    if (!layout || projects.length === 0) return null;

    let indexProjects = 0;

    return (
        <div className={s.projectsList}>
            {!defaultGrid && layout.map((module, index) => {
                let numColumns = module === '1 column' ? 1 : module === '2 columns' ? 2 : module === '3 columns' ? 3 : 0;
                if (numColumns === 0 || indexProjects >= projects.length) return null;

                const items = projects.slice(indexProjects, indexProjects + numColumns);
                indexProjects += numColumns;

                return (
                    <motion.div 
                        key={index} 
                        className={s[`columns${numColumns}`]}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        viewport={{ once: true }}
                    >
                        {items.map((project, i) => (
                            <motion.div 
                                key={i} 
                                className={s.image}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <ProjectFeaturedComponent data={project} full={numColumns === 1} />
                            </motion.div>
                        ))}
                    </motion.div>
                );
            })}

            {!defaultGrid && indexProjects < projects.length && (
                <motion.div 
                    className={s.projectsGrid}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    viewport={{ once: true }}
                >
                    {projects.slice(indexProjects).map((project, i) => (
                        <motion.div 
                            key={i} 
                            className={s.image}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            viewport={{ once: true }}
                        >
                            <ProjectFeaturedComponent data={project} />
                        </motion.div>
                    ))}
                </motion.div>
            )}

            {defaultGrid && <motion.div 
                    className={s.projectsGrid}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    viewport={{ once: true }}
                >     
                    {projects.map((project, i) => (
                        <motion.div 
                            key={i} 
                            className={s.image}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            viewport={{ once: true }}
                        >
                            <ProjectFeaturedComponent data={project} />
                        </motion.div>
                    ))}
                </motion.div>
            }
        </div>
    );
}